import { ComponentProps, memo } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { useNavigate } from 'react-router';
import { Card, Button, Typography } from 'components';
import H1 from 'components/H/H1';
import AttentionLottie from 'assets/lottie/attention.json';
import { useTypedLocation } from 'hooks/useTypedLocation';
import { DOCUMENT_UPLOAD_LATER, INCOME_PROOF, PSD2_BANKS } from 'Router/routes';
import { is352Error, usePSD2ErrorText } from './usePSD2ErrorText';

const lottieOptions = {
  loop: false,
  autoplay: true,
  animationData: AttentionLottie,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
} satisfies ComponentProps<typeof Lottie>['options'];

export default memo(function PSD2Error() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { title, description } = usePSD2ErrorText();
  const location = useTypedLocation<'PSD2Error'>();

  const hideAttachOption = is352Error(location.state?.error);

  const handleCheckOnline = () => {
    navigate(PSD2_BANKS);
  };

  const handleUpload = () => {
    navigate(INCOME_PROOF);
  };

  const handleSendByEmail = () => {
    navigate(DOCUMENT_UPLOAD_LATER);
  };

  return (
    <>
      <Helmet>
        <title>{t(`psd2_error:html_title`)}</title>
      </Helmet>
      <Card className="max-w-[585px] md:min-w-[380px] p-3.5 pb-[32px] flex flex-col rounded-none lg:rounded-lg">
        <div className="w-[75px] mx-auto">
          <Lottie options={lottieOptions} ariaRole="presentation" />
        </div>
        <H1 className="text-2xl lg:text-2xl lg:text-center">{title}</H1>
        <div className="text-center border border-[#DDE0EC] bg-[#F3F7FA] rounded-lg p-3 lg:p-6 whitespace-pre-line">
          <Typography dataTest="description" html={description} />
        </div>
        <div className="w-full flex flex-col mt-6 gap-2">
          <div>
            <h2 className="font-sofia font-bold text-[15px] leading-6">
              {t('psd2_error:select_option')}
            </h2>
          </div>
          <div className="w-full block">
            <ul className="list-none p-0 m-0 border border-[#DDE0EC] rounded-lg">
              <li className="flex-col p-3 py-6 md:py-3 md:flex-row gap-2 flex justify-between items-center border-b border-[#DDE0EC]">
                <div>
                  <Typography>{t('psd2_error:verify_online')}</Typography>
                </div>
                <div className="flex justify-end w-full lg:w-auto">
                  <Button
                    onClick={handleCheckOnline}
                    size="m"
                    dataTest="button-check-online"
                    buttonClassName="flex-1 w-full lg:w-auto"
                    className="w-full lg:w-44"
                  >
                    {t('psd2_error:verify_online_button')}
                  </Button>
                </div>
              </li>
              {!hideAttachOption && (
                <li className="flex-col p-3 py-6 md:py-3 md:flex-row gap-2 flex justify-between items-center border-b border-[#DDE0EC]">
                  <div>
                    <Typography>{t('psd2_error:verify_upload')}</Typography>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      onClick={handleUpload}
                      size="m"
                      dataTest="button-upload"
                      buttonClassName="flex-1"
                      className="w-44"
                    >
                      {t('psd2_error:verify_upload_button')}
                    </Button>
                  </div>
                </li>
              )}
              <li className="flex flex-col p-3 py-6 md:py-3 md:flex-row gap-2 justify-between items-center">
                <div>
                  <Typography>{t('psd2_error:send_email')}</Typography>
                </div>
                <div className="flex justify-end w-full lg:w-auto">
                  <Button
                    onClick={handleSendByEmail}
                    size="m"
                    dataTest="button-send-by-email"
                    buttonClassName="flex-1 w-full lg:w-auto"
                    className="w-full lg:w-44"
                  >
                    {t('psd2_error:send_email_button')}
                  </Button>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <Typography className="text-xs text-muted md:pr-8 mt-[20px] font-bold">
          {t('psd2_error:select_option_description')}
        </Typography>
      </Card>
    </>
  );
});
